import * as React from 'react';

import logger from 'src/utils/logger';

import {captureSentryException} from 'src/utils/sentry';


type Props = {
  children: React.ReactNode;
  fallback?: (
    arg1: Error,
    arg2?: (() => void) | null | undefined,
  ) => React.ReactNode;
  onError?: (arg1: Error) => unknown;
  onReset?: () => void;
  id?: unknown;
};

type State = {
  error: Error | null | undefined;
};

export default class ErrorBoundary extends React.Component<Props, State> {
  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, State, any>'.
  state: State = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): State {
    return {error};
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, State, any>'.
  componentDidCatch(error: ApiError, info: unknown) {
    if (this.props.onError) {
      this.props.onError(error);
    }

    logger.error('[Error Boundary]', error);
    const sentryFingerprint = (error as any).sentryFingerprint;

    const apiPath = error?.options?.options?.apiPath;
    const path = error?.options?.path;
    const fullApiPath = apiPath + path;
    const errorStatus = error?.response?.status;
    const IGNORE_PATHS = ['/bff/api/v1/action_metadata/events'];
    if (!(IGNORE_PATHS.includes(fullApiPath) && errorStatus === 401)) {
      captureSentryException(error, {
        level: 'error',
        tags: {
          context: 'Boundary',
          ...(sentryFingerprint ? {'autofix-demo': 'true'} : {}),
        },
        extra: {
          info,
        },
        ...(sentryFingerprint ? {fingerprint: sentryFingerprint} : {}),
      });
    }
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, State, any>'.
  componentDidUpdate(prevProps: Props) {
    if (this.state.error && this.props.id !== prevProps.id) {
      this.setState({error: null});
    }
  }

  resetErrorBoundary: () => void = () => {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.setState({error: null});
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, State, any>'.
  render(): React.ReactNode {
    if (this.state.error) {
      return this.props.fallback
        ? this.props.fallback(this.state.error, this.resetErrorBoundary)
        : null;
    }

    return this.props.children;
  }
}
